<script setup lang="ts">
import type { ScheduleEvent } from '~/types'
import { useHandleException } from '#imports'

defineOptions({
  name: 'ScheduleEventsTickerItem',
})

interface Props {
  scheduleEvent: ScheduleEvent.Model
}

const props = defineProps<Props>()

const { handleException } = useHandleException()

const loading = ref<boolean>(false)

const {
  awayTeamData,
  homeTeamData,
  id,
  eventDateFormatted,
  time,
  isStatusScheduled,
  statusText,
  scheduleEventBroadcasts,
  scheduleEventLeagueViewLinks,
} = useScheduleEvent(props.scheduleEvent)

const { fetch } = useLiveScheduleEvent(props.scheduleEvent.srId, { withBoxScore: true })

const loadBoxScore = async () => {
  loading.value = true

  try {
    await fetch()
  }
  catch (ex: unknown) {
    handleException(ex)
  }
  finally {
    loading.value = false
  }
}

onMounted(async () => {
  await loadBoxScore()
})
</script>

<template>
  <div class="schedule-events-ticker-item">
    <DashboardLink
      v-if="id"
      class="schedule-events-ticker-item__dashboard-link"
      :entity-id="id"
      entity-name="schedule-events"
    />
    <UILoadingCard v-if="loading" />
    <template v-else>
      <div class="schedule-events-ticker-item__inner">
        <div class="schedule-events-ticker-item__header">
          <div class="schedule-events-ticker-item__meta">
            <time class="schedule-events-ticker-item__date">{{ eventDateFormatted }}</time>
            <time
              v-if="time && isStatusScheduled"
              class="schedule-events-ticker-item__time"
            >
              {{ time }}
            </time>
            <span
              v-else-if="statusText"
              class="schedule-events-ticker-item__status"
            >
              {{ statusText }}
            </span>

            <ScheduleEventsTickerBroadcast
              v-for="(broadcast, index) in scheduleEventBroadcasts"
              :key="`ticker-meta-broadcast-${index}`"
              :broadcast
              class="schedule-events-ticker-item__broadcast"
            />
          </div>

          <ScheduleEventsLiveBadge
            :schedule-event
            class="schedule-events-ticker-item__live"
          />
        </div>

        <div class="schedule-events-ticker-item__teams">
          <ScheduleEventsTickerTeam
            v-if="awayTeamData"
            v-bind="awayTeamData"
            class="schedule-events-ticker-item__team"
          />
          <ScheduleEventsTickerTeam
            v-if="homeTeamData"
            v-bind="homeTeamData"
            class="schedule-events-ticker-item__team"
          />
        </div>

        <div
          v-if="scheduleEventLeagueViewLinks?.length"
          class="schedule-events-ticker-item__links"
        >
          <UIButton
            v-for="(item, index) in scheduleEventLeagueViewLinks"
            :key="`schedule-events-ticker-item-link-${index}`"
            :href="item.url"
            :target="item.target"
            variant="clear"
            appended-icon="rivet-icons:arrow-up-right"
            size="xs"
          >
            {{ item.title }}
          </UIButton>
          <ScoresLink
            :schedule-event
            variant="clear"
            appended-icon="rivet-icons:arrow-up-right"
            size="xs"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.schedule-events-ticker-item {
  &__inner {
    @apply flex flex-col px-[14px] border-t border-primary/10 relative;
    @apply xl:border-r xl:border-t-0 xl:h-[52px] xl:gap-[5px];
  }

  &__teams {
    @apply flex flex-col items-start gap-[5px] last:pb-[10px];
    @apply xl:gap-0 xl:last:pb-0;
  }

  &__links {
    @apply left-0 top-0 mt-[10px] py-[5px] border-t border-t-primary/20 bg-white h-full w-full overflow-auto flex flex-wrap gap-[10px] items-start;
    @apply xl:hidden xl:absolute xl:pt-0 xl:border-t-0 xl:px-[14px] xl:mt-0;

    &:deep(.button),
    &:deep(.button__icon) {
      @apply text-primary;
      @apply hover:text-primary/60;
    }

    &:deep(.button) {
      @apply max-xl:min-h-[24px];
    }
  }

  &__inner:hover &__links {
    @apply flex;
  }

  &__header {
    @apply flex items-center relative min-h-[24px] py-[5px];
    @apply xl:min-h-[unset] xl:py-0;
  }

  &__meta {
    @apply text-[14px] opacity-70 uppercase text-primary flex items-center leading-[1] gap-[4px];
    @apply xl:text-[12px];

    * {
      @apply first:before:hidden last:before:hidden;
      @apply before:content-[''] before:w-[2px] before:h-[2px] before:rounded-full before:bg-primary before:inline-block before:align-middle before:mr-[4px];
    }
  }

  &__broadcast {
    @apply max-xl:min-h-[24px] max-xl:inline-flex max-xl:items-center;

    :deep(.schedule-events-ticker-broadcast__link) {
      @apply max-xl:min-h-[24px] max-xl:inline-flex max-xl:items-center;
    }
  }

  &__live {
    @apply block ml-auto mb-[15px];
    @apply xl:absolute xl:right-0 xl:top-0;
  }

  &__dashboard-link {
    @apply absolute top-0 right-0 z-[2];
  }
}
</style>
